// The secure-ls backend behind `dashboardStore`'s persisted subset (contract 09 §7 "Persist").
//
// FROZEN CODEC, same reason as `features/account/storage/AccountStorage.ts` and
// `features/reports/storage/customizeStorage.ts`: v3 and v4 serve different routes on the SAME
// origin during the proxy split, so they share one localStorage. v3 constructs this store's
// secure-ls as `new SecureLS({ isCompression: false })` (v3 `stores/dashboardStore.ts:8`) — no
// `encodingType`, so the library default, Base64. Same key + a different codec means each app's
// write destroys the other's blob, so these ctor options are a wire contract, not a preference.
import SecureLS from 'secure-ls'

// secure-ls ships a webpack-UMD CJS bundle whose module.exports is
// { __esModule, default: class }. Vite's production (rolldown) CJS interop is node-mode — this
// default import receives that whole exports object — while dev/vitest (esbuild prebundle)
// receive the class itself. Resolve both to the constructor; without this, `new SecureLS(...)`
// throws "not a constructor" in production builds only, and the store silently stops persisting.
const SecureLSCtor: typeof SecureLS =
  (SecureLS as unknown as { default?: typeof SecureLS }).default ?? SecureLS

/** pinia-plugin-persistedstate storage backend (sync, string-valued). */
export interface PersistStorageLike {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

let ls: SecureLS | null = null
function secure(): SecureLS {
  return (ls ??= new SecureLSCtor({ isCompression: false }))
}

/** Backs `dashboardStore` — Base64, no compression (v3 `dashboardStore.ts:8`). */
export const dashboardPersistStorage: PersistStorageLike = {
  getItem: (key: string): string | null => {
    try {
      const value = secure().get(key)
      return value === '' || value == null ? null : (value as string)
    } catch {
      return null // tampered / decrypt failure — fail safe to "nothing cached"
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      secure().set(key, value)
    } catch (cause) {
      // A swallowed write costs one extra definitions fetch on the next reload — keep the
      // failure observable while staying fail-safe.
      console.warn('[dashboards] secure-ls write failed; definitions will not persist.', cause)
    }
  },
  removeItem: (key: string): void => secure().remove(key),
}
